import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('token')
    : null;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirm) {
      toast.error('Please fill in both fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: token,
        new_password: password
      });
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Password reset failed:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('expired')) {
        toast.error('That reset link has expired. Request a new one below.');
      } else {
        toast.error('That reset link is not valid. Request a new one below.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--navy-50)' }}>
        <div className="max-w-md w-full bg-white p-8 rounded-lg border border-gray-200 shadow-sm text-center space-y-4">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            Link not valid
          </h1>
          <p className="text-gray-600">
            This page needs the reset link from your email. If yours has expired or got cut
            off, request a fresh one.
          </p>
          <Button
            onClick={() => navigate('/forgot-password')}
            className="w-full py-6 text-base font-medium rounded-lg transition-colors duration-200"
            style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
          >
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--navy-50)' }}>
      <div className="max-w-md w-full bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            Choose a new password
          </h1>
          <p className="text-gray-600">
            Pick something at least 8 characters long.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
              New password
            </label>
            <Input
              data-testid="reset-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
              Confirm new password
            </label>
            <Input
              data-testid="reset-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            data-testid="reset-submit-btn"
            disabled={loading}
            className="w-full py-6 text-base font-medium rounded-lg transition-colors duration-200"
            style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
          >
            {loading ? 'Please wait...' : 'Set new password'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-navy-900 transition-colors duration-200"
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
